import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogContent({
  className,
  title,
  description,
  footer,
  size = 'md',
  tone = 'paper',
  children,
}: {
  className?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * `paper` ist die Vorgabe. `ink` macht den Dialog zur dunklen Tafel – für
   * Ansichten, in denen ein Bild die Hauptsache ist: auf hellem Grund
   * beurteilt niemand einen Bildausschnitt, das Papier drumherum zieht das
   * Auge und verschiebt den Helligkeitseindruck.
   */
  tone?: 'paper' | 'ink';
  children?: React.ReactNode;
}) {
  const widths = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-xl',
    lg: 'sm:max-w-3xl',
    xl: 'sm:max-w-5xl',
  } as const;

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fb-overlay fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px]" />
      <DialogPrimitive.Content
        className={cn(
          'fb-dialog fixed z-50 flex flex-col overflow-hidden shadow-float',
          // `fb-panel`: dieselbe Umbelegung wie bei `InkPanel` – siehe dort.
          tone === 'ink' ? 'fb-panel bg-panel' : 'bg-surface',
          // Mobil: Sheet von unten. Ab sm: zentrierter Dialog.
          'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-3xl border-t',
          tone === 'ink' ? 'border-panel-line' : 'border-line',
          'sm:inset-auto sm:top-1/2 sm:left-1/2 sm:max-h-[88dvh] sm:w-[calc(100%-2rem)]',
          'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-card sm:border',
          widths[size],
          className,
        )}
      >
        <div
          className={cn(
            'flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6',
            tone === 'ink' ? 'border-panel-line' : 'border-line',
          )}
        >
          <div className="min-w-0">
            <DialogPrimitive.Title
              className={cn(
                'font-display text-[19px] leading-6 font-medium',
                tone === 'ink' ? 'text-panel-ink' : 'text-ink',
              )}
            >
              {title}
            </DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description
                className={cn(
                  'mt-1 text-[13px] leading-5',
                  tone === 'ink' ? 'text-panel-ink-2' : 'text-ink-3',
                )}
              >
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="iconSm" aria-label="Schließen">
              <X aria-hidden />
            </Button>
          </DialogPrimitive.Close>
        </div>

        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>

        {footer ? (
          <div
            className={cn(
              'flex flex-wrap items-center justify-end gap-2 border-t px-5 py-4 sm:px-6',
              tone === 'ink' ? 'border-panel-line bg-panel' : 'border-line bg-elevated',
            )}
          >
            {footer}
          </div>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
